import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import { useTimer } from 'react-timer-hook'
import { IKDTKlass } from 'stores/courses/types'

import CloudKlass3rd from '../CloudKlass3rd'
import ApplyButton from './ApplyButton'
import UnityKlass5th from '../UnityKlass5th'
import DataKlass5th from '../DataAnalysis5th'
import Besp16th from '../Besp16th'
import FrontKlass14th from '../FrontKlass14th'
import GrowthKlass2nd from '../Growth2nd'
import AOSKlass4th from '../AOSKlass4th'
import Bebj17th from '../Bebj17th'
import DesignSchool5th from '../DesignSchool5th'
import { usePreNoticeInfoQuery } from 'app/pre-notice/_queries/usePreNoticeInfoQuery'

interface INextStatus {
  [status: string]: string
}

interface IFullPublishingDetailProps {
  course: IKDTKlass
  submitType: {
    [status: string]: {
      disabled: boolean
      name: string
      period?: string
      handler: () => void
    }
  }
  status: string
}

const FullPublishingDetail = ({ course, submitType, status }: IFullPublishingDetailProps) => {
  const expiryTimestamp = new Date(course.applyEndedAt)

  const { data: preNoticeInfo } = usePreNoticeInfoQuery()
  const { minutes, hours, days, seconds, restart } = useTimer({
    expiryTimestamp: expiryTimestamp,
    onExpire: () => console.warn('onExpire called'),
  })
  const isLeftApplyDurationOneDays = days === 0
  const isEarlyDate = dayjs().isBefore(course.applyStartedAt)
  const isCloud = course.id.includes('kdt-cld')
  const isUnity = course.id.includes('kdt-ugm')
  const isData = course.id.includes('kdt-dataanalysis')
  const isBackendp = course.id.includes('kdt-backendp')
  const isGrowth = course.id.includes('kdt-growth')
  const isFront = course.id.includes('kdt-front')
  const isAos = course.id.includes('kdt-aos')
  const isBackendj = course.id.includes('kdt-backendj')
  const isDesign = course.id.includes('kdt-design')

  const curStatus: INextStatus = {
    early: 'early',
    assignment: 'assignment',
    assignmentReview: 'assignmentReview',
    participation: dayjs().isBefore(course.startedAt) ? 'participation' : 'ing',
    participationReview: dayjs().isBefore(course.startedAt) ? 'participationReview' : 'ing',
    ing: 'ing',
    end: 'end',
  }

  const format = dayjs
    .duration({
      days,
      hours,
      minutes,
      seconds,
    })
    .format(isLeftApplyDurationOneDays ? 'HH:mm:ss' : 'D일 HH:mm:ss')

  const message = useMemo(() => {
    if (isEarlyDate) {
      return (
        <p className="text-sm font-semibold text-label-normal lg:text-lg">
          {preNoticeInfo?.bannerText}
        </p>
      )
    } else if (course.kdtAttr.promotionType === 'd_day') {
      return (
        <p className="text-sm font-semibold text-label-normal lg:text-lg">
          지원 마감까지 <span className="text-primary-normal">{format}</span> 남았어요.
        </p>
      )
    } else if (course.kdtAttr.promotionType === 'apply_count') {
      return (
        <p className="text-sm font-semibold text-label-normal lg:text-lg">
          현재 <span className="text-primary-normal">{course.kdtAttr.promotionApplyCount}명이</span>{' '}
          지원중이에요.
        </p>
      )
    } else if (course.kdtAttr.promotionType === 'custom_text') {
      return (
        <p className="text-sm font-semibold text-label-normal lg:text-lg">
          {course.kdtAttr.promotionText}
        </p>
      )
    } else {
      return <p className="text-sm font-semibold text-label-normal lg:text-lg">{course.title}</p>
    }
  }, [course, format])

  useEffect(() => {
    restart(expiryTimestamp)
  }, [course.id])

  const { applyStartedAt, applyEndedAt, startedAt, endedAt, totalSeconds } = course

  return (
    <main className="relative min-h-screen w-full min-w-[360px]" id="pre-notice-drawer">
      {/* 퍼블리싱 contents */}
      {isCloud && <CloudKlass3rd />}
      {isUnity && (
        <UnityKlass5th
          applyStartedAt={applyStartedAt}
          applyEndedAt={applyEndedAt}
          startedAt={startedAt ?? ''}
          endedAt={endedAt ?? ''}
          totalSeconds={totalSeconds ?? 0}
        />
      )}
      {isData && <DataKlass5th />}
      {isBackendp && <Besp16th />}
      {isFront && <FrontKlass14th />}
      {isGrowth && (
        <GrowthKlass2nd
          applyStartedAt={applyStartedAt}
          applyEndedAt={applyEndedAt}
          startedAt={startedAt ?? ''}
          endedAt={endedAt ?? ''}
          totalSeconds={totalSeconds ?? 0}
        />
      )}
      {isAos && <AOSKlass4th />}
      {isBackendj && (
        <Bebj17th
          applyStartedAt={applyStartedAt}
          applyEndedAt={applyEndedAt}
          startedAt={startedAt ?? ''}
          endedAt={endedAt ?? ''}
          totalSeconds={totalSeconds ?? 0}
        />
      )}
      {isDesign && <DesignSchool5th />}

      {/* 하단 floating */}
      <div className="fixed bottom-0 left-0 z-75 flex w-full min-w-[360px] transform flex-col items-center justify-between gap-2.5 bg-white px-5 pb-4 pt-3 backdrop-blur lg:bottom-6 lg:left-1/2 lg:w-[768px] lg:-translate-x-1/2 lg:flex-row lg:rounded-md lg:px-6 lg:py-2.5 lg:shadow-[0_4px_16px_0_rgba(0,0,0,0.2)]">
        <div className="flex items-center justify-center">{message}</div>
        <div className="w-full lg:max-w-[160px]">
          <ApplyButton
            klassId={course.id}
            disabled={submitType[curStatus[status]]?.disabled}
            handler={submitType[curStatus[status]]?.handler}
            name={submitType[curStatus[status]]?.name}
            isApplying={!isEarlyDate}
          />
        </div>
      </div>
    </main>
  )
}
export default FullPublishingDetail
